import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {
  Card,
  CardHeader,
  CardTitle,
  CardActions,
  CardContent,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {Tooltip} from '@spaced-out/ui-design-system/lib/components/Tooltip';
import {Link} from '@spaced-out/ui-design-system/lib/components/Link';
import {Separator} from '@spaced-out/ui-design-system/lib/components/Separator';
import {
  BodyMediumBold,
  BodyMedium,
  SubTitleSmall,
  SubTitleExtraSmall,
  SubTitleMedium,
  ButtonTextMedium,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {TruncatedTextWithTooltip} from '@spaced-out/ui-design-system/lib/components/TruncatedTextWithTooltip';
import {Table} from '@spaced-out/ui-design-system/lib/components/Table';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';

import User from 'src/components/crm/lib/user/user';

import {formatMomentDateWithFormat} from 'src/utils/date-time';

import {sizeFluid} from '@spaced-out/ui-design-system/lib/styles/variables/_size';

import ViewCustomFields from 'src/components/crm/lib/custom-fields/view-custom-fields';
import {convertFromRaw} from 'draft-js';
import {stateToHTML} from 'draft-js-export-html';
import css from './evaluation-subjective-questions-view.css';
import editorCss from 'src/components/crm/jobs/job-info/about/details.css';
import type {QuestionsItem, Section, ParametersItem} from 'src/types/crm';


interface AnswersItem {
  questionId: number;
  answer: string;
}
interface SectionsItem {
  section: Section;
  questions: QuestionsItem[];
  parameters: ParametersItem[];
}

const EvaluationSubjectiveQuestionsView = ({
  // @ts-expect-error - TS7031 - Binding element 'subjectiveSections' implicitly has an 'any' type.
  subjectiveSections,
  // @ts-expect-error - TS7031 - Binding element 'answers' implicitly has an 'any' type.
  answers,
  // @ts-expect-error - TS7031 - Binding element 'token' implicitly has an 'any' type.
  token,
  // @ts-expect-error - TS7031 - Binding element 'tokenId' implicitly has an 'any' type.
  tokenId,
  // @ts-expect-error - TS7031 - Binding element 'review' implicitly has an 'any' type.
  review,
  // @ts-expect-error - TS7031 - Binding element 'comment' implicitly has an 'any' type.
  comment,
  // @ts-expect-error - TS7031 - Binding element 'feedbackConfiguration' implicitly has an 'any' type.
  feedbackConfiguration,
  // @ts-expect-error - TS7031 - Binding element 'reviewStatus' implicitly has an 'any' type.
  reviewStatus,
  scorecardStepSections = [],
  feedbackScores = [],
  // @ts-expect-error - TS7031 - Binding element 'hideScorecard' implicitly has an 'any' type.
  hideScorecard,
  // @ts-expect-error - TS7031 - Binding element 'step' implicitly has an 'any' type.
  step,
  // @ts-expect-error - TS7031 - Binding element 'forPdf' implicitly has an 'any' type.
  forPdf,
}) => {
  const {t, language} = useLabelTranslation();

  // const {hasNote} = feedbackConfiguration;
  // const hideOverallRecommendation = step?.hideOverallRecommendation;

  const customRenderOptions = {
    blockRenderers: {
      // @ts-expect-error - TS7006 - Parameter 'block' implicitly has an 'any' type.
      unstyled: (block) => `<span>${block.getText()}</span>`,
    },
    defaultBlockTag: 'span',
  } as const;

  const removeEmptyBlocks = (rawContent: any) => {
    rawContent.blocks = rawContent.blocks.filter(
      // @ts-expect-error - TS7006 - Parameter 'block' implicitly has an 'any' type.
      (block) => block.text.trim() !== '',
    );
    return rawContent;
  };

  const renderAsHTML = (jsonString: any) => {
    if (!jsonString) {
      return '';
    }
    try {
      const rawContent = JSON.parse(jsonString);
      // const cleanedContent = removeEmptyBlocks(rawContent);
      const contentState = convertFromRaw(rawContent);
      return stateToHTML(contentState);
    } catch (error: any) {
      return jsonString || '';
    }
  };

  let scorecardParameters: Array<never> = [];
  scorecardStepSections.forEach((section) => {
    // @ts-expect-error - TS2322 - Type 'any[]' is not assignable to type 'never[]'. | TS2339 - Property 'parameters' does not exist on type 'never'.
    scorecardParameters = [...scorecardParameters, ...section.parameters];
  });

  const parametersNameMap = scorecardParameters.reduce<Record<string, any>>(
    (a, b) => {
      // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'never'. | TS2339 - Property 'name' does not exist on type 'never'.
      a[b.id] = b.name;
      return a;
    },
    {},
  );

  const scoreCardData = feedbackScores.map(({parameterId, score, remark}) => {
    return {parameter: parametersNameMap[parameterId], score, remark};
  });

  const tableEntries = React.useMemo(() => {
    if (!scorecardStepSections || !review?.feedbackScores) {
      return [];
    }

    return scorecardStepSections.flatMap((section) => {
      // @ts-expect-error - TS2339 - Property 'parameters' does not exist on type 'never'.
      if (!section?.parameters) {
        return [];
      }

      return (
        // @ts-expect-error - TS2339 - Property 'parameters' does not exist on type 'never'.
        section.parameters
          // @ts-expect-error - TS7006 - Parameter 'parameter' implicitly has an 'any' type.
          .map((parameter) => {
            const feedbackScore = review?.feedbackScores.find(
              (score: any) => score.parameterId === parameter.id,
            );

            if (!feedbackScore) {
              return null;
            }

            return {
              label: parameter.name,
              rating: feedbackScore.score,
              comment: feedbackScore.remark || '-',
              id: parameter.id,
            };
          })
          .filter(Boolean)
      );
    });
  }, [scorecardStepSections, review]);

  const type = review?.interviewSchedule ? 'interview' : 'applicant';

  const cardData = React.useMemo(() => {
    if (type === 'applicant') {
      return [
        {
          icon: 'calendar',
          text: `${t(
            'Submitted_on',
            'Submitted on',
          )}: ${formatMomentDateWithFormat(review?.createdOn, 'L', language)}`,
          id: 'createdOn',
        },
        {
          icon: 'user',
          text: <User userId={review?.reviewerId} />,
          id: 'reviewerName',
        },
      ];
    }

    const startDate = review?.interviewSchedule?.startDate
      ? formatMomentDateWithFormat(
          review.interviewSchedule.startDate,
          'L',
          language,
        )
      : '';
    const endDate = review?.interviewSchedule?.endDate
      ? formatMomentDateWithFormat(
          review.interviewSchedule.endDate,
          'L',
          language,
        )
      : '';

    return [
      {
        icon: 'calendar',
        text: formatMomentDateWithFormat(
          review?.interviewSchedule?.endDate,
          'L',
          language,
        ),
        id: 'endDate',
      },
      {
        icon: 'clock',
        text:
          startDate && endDate
            ? `${startDate} - ${endDate} ${t('IST', 'IST')}`
            : startDate
            ? `${startDate} ${t('IST', 'IST')}`
            : endDate
            ? `${endDate} ${t('IST', 'IST')}`
            : t('NA', 'N/A'),
        id: 'time',
      },
      {
        icon: 'user',
        text: <User userId={review?.reviewerId} />,
        id: 'user',
      },
    ];
  }, [review]);

  const commentHTML = renderAsHTML(comment);

  return (
    <>
      <Card classNames={{wrapper: css.card}}>
        <div className={css.cardHeader}>
          <BodyMediumBold>{`${t('Stage_Name', 'Stage Name')}:`}</BodyMediumBold>
          <BodyMedium>{review?.stageName || '-'}</BodyMedium>
        </div>
        <CardContent className={css.cardContent}>
          <Card
            classNames={{
              wrapper: css.innerCard,
            }}
            paddingTop="small"
            paddingBottom="small"
          >
            {cardData.map((item) => (
              <div key={item.id} className={css.innerCompletedCard}>
                <Icon name={item.icon} size="small" />
                <SubTitleExtraSmall color="secondary">
                  <TruncatedTextWithTooltip
                    tooltip={{
                      bodyMaxLines: 3,
                      elevation: 'modal',
                    }}
                  >
                    {item.text}
                  </TruncatedTextWithTooltip>
                </SubTitleExtraSmall>
              </div>
            ))}
          </Card>
        </CardContent>
      </Card>
      <div className={css.fieldContainer}>
        <SubTitleMedium>
          {t('Overall_Recommendation', 'Overall Recommendation')}
        </SubTitleMedium>
        <ButtonTextMedium>
          <TruncatedTextWithTooltip
            tooltip={{
              bodyMaxLines: 3,
              elevation: 'modal',
            }}
          >
            {review?.reviewStatus || '-'}
          </TruncatedTextWithTooltip>
        </ButtonTextMedium>
      </div>
      <div className={css.fieldContainer}>
        <SubTitleMedium>
          {t('Overall_Comment', 'Overall Comment')}
        </SubTitleMedium>
        <div
          className={css.richTextEditor}
          dangerouslySetInnerHTML={{__html: commentHTML || '-'}}
        />
      </div>
      <div className={css.separator}>
        <Separator
          classNames={{
            wrapper: css.separator,
          }}
          height="1px"
          orientation="horizontal"
          width={sizeFluid}
        />
      </div>
      <div className={css.scorecard}>
        <BodyMediumBold>{t('Scorecard', 'Scorecard')}</BodyMediumBold>
        <SubTitleSmall color="secondary">
          {`${t('Average_Rating', 'Average Rating')}: ${
            review?.averageFeedbackScore?.toFixed(1) || '-'
          }`}
        </SubTitleSmall>
      </div>
      <div>
        <Table
          borderRadius="12px"
          entries={tableEntries}
          classNames={{
            wrapper: css.tableWrapper,
          }}
          headers={[
            {
              label: t('Parameters', 'Parameters'),
              key: 'label',
              sortable: false,
              className: css.column,
            },
            {
              label: t('Rating', 'Rating'),
              key: 'rating',
              sortable: false,
              className: css.midColumn,
            },
            {
              label: t('Remarks', 'Remarks'),
              key: 'comment',
              sortable: false,
              className: css.column,
            },
          ]}
        />
      </div>
      {subjectiveSections?.length > 0 ? (
        <div className={css.separator}>
          <Separator
            classNames={{
              wrapper: css.separator,
            }}
            height="1px"
            orientation="horizontal"
            width={sizeFluid}
          />
        </div>
      ) : null}
      {subjectiveSections?.map(
        (section: SectionsItem, sectionIndex: number) => (
          <div
            key={section.section?.id || sectionIndex}
            className={css.fieldContainer}
          >
            <SubTitleMedium>{section.section?.title}</SubTitleMedium>
            {section.section?.questions?.map(
              (question: QuestionsItem, questionIndex: number) => {
                const questionId = question.id ?? questionIndex;
                const questionFieldType = question.fieldType ?? 'TEXT';
                const questionTag = question.query ?? '';
                const field = {
                  id: questionId,
                  fieldType: questionFieldType,
                  questionTag,
                  value:
                    review?.answers?.find(
                      (a: AnswersItem) => a.questionId === questionId,
                    )?.answer || '-',
                  options: question.options || [],
                  customFieldId: questionId,
                  users: [],
                };
                return (
                  <div key={field.id} className={css.fieldContainer}>
                    <BodyMediumBold>{`${t('Question', 'Question')} ${
                      questionIndex + 1
                    }. ${field.questionTag}`}</BodyMediumBold>
                    <ViewCustomFields field={field} />
                  </div>
                );
              },
            )}
          </div>
        ),
      )}
    </>
  );
};

export default EvaluationSubjectiveQuestionsView;
